<template>
  <div class="category-services-page">
    <v-container class="py-5">
      <div class="page-header mb-5">
        <v-btn icon class="back-btn" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="eyebrow">Category</div>
          <h1 class="title">{{ categoryName }}</h1>
        </div>
      </div>

      <div v-if="loading" class="loading-wrap">
        <div class="text-center">
          <v-progress-circular indeterminate color="#f46523" size="54" />
          <p class="mt-3 grey--text">Loading services...</p>
        </div>
      </div>

      <v-row v-else-if="locationRequired" justify="center" class="py-12">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="64" color="#f46523">mdi-map-marker-alert-outline</v-icon>
          <h3 class="mt-4">Location permission required</h3>
          <p class="grey--text mt-2">
            {{ locationMessage }}
          </p>
          <v-btn color="#f46523" dark class="mt-4" @click="handleEnableLocationClick">
            Enable Location
          </v-btn>
        </v-col>
      </v-row>

      <v-row v-else-if="services.length" class="services-grid" justify="center">
        <v-col
          v-for="service in services"
          :key="service._id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card class="service-card" outlined @click="openService(service)">
            <v-img
              :src="service.image?.[0] || placeholderImage"
              height="180"
              cover
            />
            <v-card-text class="pa-4 text-center">
              <h3 class="service-name">{{ service.title }}</h3>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else justify="center" class="py-12">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="64" color="#f46523" class="opacity-30">
            mdi-alert-circle-outline
          </v-icon>
          <h3 class="mt-4">No services available</h3>
          <p class="grey--text mt-2">
            We couldn't find services for this category right now.
          </p>
          <v-btn color="#f46523" text class="mt-3" @click="$router.back()">
            Back to Categories
          </v-btn>
        </v-col>
      </v-row>

      <LocationPermissionDialog v-model="showLocationHelpDialog" />
    </v-container>
  </div>
</template>

<script>
import { fetchServicesByCategory } from "@/services/api";
import LocationPermissionDialog from "@/components/LocationPermissionDialog.vue";

export default {
  name: "CategoryServicesView",
  components: {
    LocationPermissionDialog,
  },
  data() {
    return {
      services: [],
      loading: true,
      locationRequired: false,
      locationMessage:
        "Please allow location access to view available services in this category.",
      showLocationHelpDialog: false,
      openHelpOnDenied: false,
      placeholderImage: "https://via.placeholder.com/600x400?text=Service+Image",
    };
  },
  computed: {
    categoryId() {
      return this.$route.params.categoryId;
    },
    categoryName() {
      const fromParams = this.$route.params.categoryName;
      if (fromParams) return decodeURIComponent(fromParams);

      const fromSession = sessionStorage.getItem("selectedCategory");
      if (fromSession) {
        try {
          const parsed = JSON.parse(fromSession);
          return parsed?.name || "Services";
        } catch (error) {
          return "Services";
        }
      }
      return "Services";
    },
  },
  mounted() {
    this.requestLocationAndLoad();
  },
  methods: {
    async handleEnableLocationClick() {
      const isBlocked = await this.isLocationPermissionBlocked();
      if (isBlocked) {
        this.showLocationHelpDialog = true;
        return;
      }

      this.openHelpOnDenied = true;
      this.requestLocationAndLoad();
    },

    async isLocationPermissionBlocked() {
      try {
        if (!navigator.permissions?.query) return false;
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        return permission.state === "denied";
      } catch (error) {
        return false;
      }
    },

    requestLocationAndLoad() {
      this.locationMessage =
        "Please allow location access to view available services in this category.";
      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.loading = false;
        return;
      }

      if (!navigator.geolocation) {
        this.locationRequired = true;
        this.locationMessage = "Geolocation is not supported in this browser.";
        this.loading = false;
        return;
      }

      this.loading = true;
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          this.openHelpOnDenied = false;
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            allowed: true,
            error: null,
          };
          localStorage.setItem("userLocation", JSON.stringify(location));
          sessionStorage.setItem("userLocation", JSON.stringify(location));
          this.locationRequired = false;
          this.locationMessage =
            "Please allow location access to view available services in this category.";
          await this.loadServices(location);
        },
        (error) => {
          this.locationRequired = true;
          this.locationMessage =
            error.code === error.PERMISSION_DENIED
              ? "Location permission is blocked. Enable it in your browser site settings and try again."
              : "Unable to detect your location right now. Please try again.";
          if (error.code === error.PERMISSION_DENIED && this.openHelpOnDenied) {
            this.showLocationHelpDialog = true;
          }
          this.openHelpOnDenied = false;
          this.loading = false;
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    },

    async loadServices(location) {
      try {
        let response;
        try {
          response = await fetchServicesByCategory({
            categoryId: this.categoryId,
            page: 1,
            pageSize: 50,
            lat: location.latitude,
            lng: location.longitude,
          });
        } catch (locationError) {
          if (locationError.response?.status !== 500) throw locationError;
          response = await fetchServicesByCategory({
            categoryId: this.categoryId,
            page: 1,
            pageSize: 50,
          });
        }

        this.services = response?.data?.data?.services || [];
      } catch (error) {
        console.error("Failed to load services by category", error);
        this.services = [];
      } finally {
        this.loading = false;
      }
    },

    openService(service) {
      sessionStorage.setItem("selectedService", JSON.stringify(service));
      this.$router.push({
        name: "CategoryDetailedView",
        params: { serviceId: service._id },
      });
    },
  },
};
</script>

<style scoped>
.category-services-page {
  min-height: 100vh;
  background: #f8fafc;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid #f0f2f5;
}

.back-btn {
  background: #fff3ed;
  border-radius: 10px;
}

.eyebrow {
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 700;
  color: #f46523;
}

.title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #111827;
}

.service-card {
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
}

.service-name {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.loading-wrap {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 600px) {
  .title {
    font-size: 22px;
  }
}
</style>
